import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"
import { Link, useRouterState } from "@tanstack/react-router"
import { Activity, Bike, LayoutGrid, PackageOpen, PanelLeftClose, PanelLeftOpen, Users } from "lucide-react"
import { useState } from "react"

type Item = { label: string; to: string; icon: any }

const views: Item[] = [
  { label: "Dashboard", to: "/manager/dashboard", icon: LayoutGrid },
]

const data: Item[] = [
  { label: "Employee Status & Performance", to: "/manager/employee-performance", icon: Users },
  { label: "Inventory", to: "/manager/inventory", icon: PackageOpen },
  { label: "Sales & Revenue", to: "/manager/sales-and-revenue", icon: Bike },
  { label: "Prescriptions", to: "/manager/prescriptions", icon: Activity },
]

export default function WorkspaceSidebar() {
  const router = useRouterState()
  const path = router.location.pathname
  const [collapsed, setCollapsed] = useState(false)

  return (
    <div className={`border-r bg-[#FBFBFB] h-full shrink-0 relative transition-all duration-300 ease-in-out ${collapsed ? 'w-14' : 'w-64'}`}>
      <div className="p-3 flex items-center justify-between">
        {!collapsed && <h2 className="text-base font-semibold">Manager</h2>}
        <button
          className="h-7 w-7 grid place-items-center rounded-md border bg-white"
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          onClick={() => setCollapsed(!collapsed)}
        >
          {collapsed ? <PanelLeftOpen size={14} /> : <PanelLeftClose size={14} />}
        </button>
      </div>
      {collapsed ? (
        <TooltipProvider>
          <div className="flex flex-col items-center gap-1 py-1">
            {[...views, ...data].map((item, i) => {
              const Icon = item.icon
              const active = path.startsWith(item.to)
              return (
                <Tooltip key={i}>
                  <TooltipTrigger asChild>
                    <Link
                      to={item.to}
                      className={`h-7 w-7 grid place-items-center rounded-md transition-colors duration-200 ${active ? 'bg-blue-50 text-blue-600' : 'text-gray-700 hover:bg-white hover:text-foreground'}`}
                      preload="intent"
                    >
                      <Icon size={14} />
                    </Link>
                  </TooltipTrigger>
                  <TooltipContent side="right" className="text-[11px]">
                    {item.label}
                  </TooltipContent>
                </Tooltip>
              )
            })}
          </div>
        </TooltipProvider>
      ) : (
        <div className="px-3">
          <Section label="Views">
            {views.map((item) => (
              <Row key={item.label} item={item} active={path.startsWith(item.to)} />
            ))}
          </Section>
          <Section label="Data">
            {data.map((item) => (
              <Row key={item.label} item={item} active={path.startsWith(item.to)} />
            ))}
          </Section>
        </div>
      )}
    </div>
  )
}

function Section({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="mb-4">
      <div className="text-xs text-muted-foreground mb-2">{label}</div>
      <div className="flex flex-col gap-1">{children}</div>
    </div>
  )
}

function Row({ item, active }: { item: Item; active: boolean }) {
  const Icon = item.icon
  return (
    <Link
      to={item.to}
      preload="intent"
      className={`flex items-center gap-2 px-3 py-1.5 rounded-2xl transition-colors duration-200 ${active ? 'bg-blue-50 text-blue-600' : 'text-gray-700 hover:bg-white/70'}`}
    >
      <Icon size={14} />
      <span className="text-xs">{item.label}</span>
    </Link>
  )
}